export type CancelCredentials = {
  cancel_reason: string;
  status: string;
};

export const usePostRequests = () => {
  const { apiVersion } = useRuntimeConfig().public;
  const { genderQuery } = useServiceGender();

  const servicesAvailableTimes = async (
    location_identifier: string,
    credentials: FormData
  ) =>
    await $larafetch(
      `/api/${apiVersion}/${location_identifier}/services-available-time`,
      {
        method: "post",
        query: genderQuery.value,
        body: credentials,
      }
    );

  const employeeAvailableTimes = async (
    location_identifier: string,
    employee_id: number | string,
    credentials: FormData
  ) =>
    await $larafetch(
      `/api/${apiVersion}/${location_identifier}/employees/${employee_id}/available-time`,
      {
        method: "post",
        query: genderQuery.value,
        body: credentials,
      }
    );

  const employeeServicesAvailableTimes = async (
    location_identifier: string,
    employee_id: number | string,
    credentials: FormData,
    query?: Record<string, string> | string,
  ) => {
    const extraQuery =
      typeof query === "string"
        ? Object.fromEntries(new URLSearchParams(query))
        : query ?? {};
    return await $larafetch(
      `/api/${apiVersion}/${location_identifier}/employees/${employee_id}/services-available-time`,
      {
        method: "post",
        query: { ...genderQuery.value, ...extraQuery },
        body: credentials,
      },
    );
  };

  const anyEmployeeAvailableTimes = async (
    location_identifier: string,
    credentials: FormData
  ) =>
    await $larafetch(
      `/api/${apiVersion}/${location_identifier}/any-employee-available-time`,
      {
        method: "post",
        query: genderQuery.value,
        body: credentials,
      }
    );

  const bookingSummary = async (
    location_identifier: string,
    credentials: FormData
  ) =>
    await $larafetch(
      `/api/${apiVersion}/${location_identifier}/booking-summary`,
      {
        method: "post",
        query: genderQuery.value,
        body: credentials,
      }
    );

  const updateProfile = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/account/update`, {
      method: "post",
      body: credentials,
    });

  const changeMobile = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/account/change-mobile`, {
      method: "post",
      body: credentials,
    });

  const changeEmail = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/account/change-email`, {
      method: "post",
      body: credentials,
    });

  const checkChangeMobileToken = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(
      `/api/${apiVersion}/account/change-mobile/check-verification-token`,
      {
        method: "post",
        body: credentials,
      }
    );

  const checkEmailVerificationCode = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(
      `/api/${apiVersion}/account/change-email/check-verification-code`,
      {
        method: "post",
        body: credentials,
      }
    );

  const resendEmailVerificationCode = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(
      `/api/${apiVersion}/account/change-email/resend-verification-code`,
      {
        method: "post",
        body: credentials,
      }
    );

  const cancelBooking = async (
    location_identifier: string,
    booking_id: number,
    credentials: CancelCredentials
  ) =>
    await $larafetch(
      `/api/${apiVersion}/${location_identifier}/booking/${booking_id}/cancel`,
      {
        method: "post",
        body: credentials,
      }
    );

  const newBooking = async (
    location_identifier: string,
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/${location_identifier}/new-booking`, {
      method: "post",
      query: genderQuery.value,
      body: credentials,
    });

  const createBusiness = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/validate-request-demo`, {
      method: "post",
      body: credentials,
    });

  const storeBusiness = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/request-demo/store`, {
      method: "post",
      body: credentials,
    });

  const contactUs = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/contact-us`, {
      method: "post",
      body: credentials,
    });

  const contactSales = async (
    credentials: any // eslint-disable-line @typescript-eslint/no-explicit-any
  ) =>
    await $larafetch(`/api/${apiVersion}/contact-sales/store`, {
      method: "post",
      body: credentials,
    });

  return {
    servicesAvailableTimes,
    employeeAvailableTimes,
    employeeServicesAvailableTimes,
    anyEmployeeAvailableTimes,
    bookingSummary,
    updateProfile,
    changeMobile,
    checkChangeMobileToken,
    changeEmail,
    checkEmailVerificationCode,
    resendEmailVerificationCode,
    cancelBooking,
    newBooking,
    createBusiness,
    storeBusiness,
    contactUs,
    contactSales,
  };
};
